import { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import "./TitleSlide.scss";
import { animated } from "@react-spring/web";
import { useSearchParams } from "react-router-dom";
import { useAnimations } from "./TitleSlide.service.js";
import { useAnnounceSlide } from "./DataViz.service.js";
import enStrings from "../../services/strings.en.json";
import enDIYStrings from "../../services/strings.en.diy.json";
import esDIYStrings from "../../services/strings.es.diy.json";
import { getString } from "../../services/stringsHelpers.js";
import { TopicConfig } from "../../models/topicConfig.js";

/**
 * @component
 * @param {Object} args
 * @param {TopicConfig} args.topicConfig
 * @param {React.MutableRefObject<(() => Promise<boolean[]>) | undefined>} args.animationFnRef
 * @param {number} args.intervalDuration
 */
function TitleSlide({ topicConfig, animationFnRef, intervalDuration }) {
  const {
    subTitle,
    dataViz: { customText },
  } = topicConfig;
  let textureSrc = "";
  let personaSrc = "";
  let orientation = "left";
  if (
    topicConfig.dataViz.images &&
    typeof topicConfig.dataViz.images !== "string"
  ) {
    if (topicConfig.dataViz.images.texture) {
      textureSrc = topicConfig.dataViz.images.texture.src;
    }
    if (topicConfig.dataViz.images.persona) {
      personaSrc = topicConfig.dataViz.images.persona.src;
      orientation = topicConfig.dataViz.images.persona.orientation;
    }
  }
  const [params] = useSearchParams();
  const isDIY = params.get("diy");
  const { styles, intros, resets } = useAnimations();
  useAnnounceSlide({
    itemConfig: topicConfig,
    strings: isDIY ? enDIYStrings : enStrings,
  });

  useEffect(() => {
    intros.textEn();
    intros.persona();
    intros.texture();

    if (isDIY) {
      setTimeout(() => {
        resets.textEn().then(() => intros.textEs());
      }, intervalDuration / 2);
    }
    // eslint-disable-next-line no-param-reassign
    animationFnRef.current = () => {
      const promises = [resets.persona(), resets.texture()];
      if (isDIY) {
        promises.push(resets.textEs());
      } else {
        promises.push(resets.textEn());
      }
      return Promise.all(promises);
    };
  }, []);
  const containerRef = useRef(/** @type {HTMLDivElement | null} */ (null));

  let containerHeight = window.innerHeight;
  if (containerRef.current) {
    const { height } = containerRef.current.getBoundingClientRect();
    containerHeight = height;
  }
  const computedStyles = {
    fontSize: `${containerHeight * 0.1}px`,
  };
  return (
    <div
      className={`dataviz__title-slide__container dataviz__title-slide__container--${orientation}`}
      ref={containerRef}
    >
      <animated.img
        src={textureSrc}
        alt=""
        className={`dataviz__title-slide__bg dataviz__title-slide__bg--${orientation}`}
        style={styles.texture}
      />
      <animated.img
        src={personaSrc}
        alt=""
        className={`dataviz__title-slide__persona dataviz__title-slide__persona--${orientation}`}
        style={styles.persona}
      />
      <animated.h1
        className={`dataviz__title-slide__copy dataviz__title-slide__copy--${orientation}`}
        style={{
          ...styles.textEn,
          ...computedStyles,
        }}
      >
        {getString(customText || subTitle, isDIY ? enDIYStrings : enStrings)}
      </animated.h1>
      {isDIY ? (
        <animated.h1
          className={`dataviz__title-slide__copy dataviz__title-slide__copy--${orientation}`}
          style={{
            ...styles.textEs,
            ...computedStyles,
          }}
        >
          {getString(customText || subTitle, esDIYStrings)}
        </animated.h1>
      ) : null}
    </div>
  );
}

TitleSlide.propTypes = {
  topicConfig: PropTypes.instanceOf(TopicConfig).isRequired,
  intervalDuration: PropTypes.number.isRequired,
  animationFnRef: PropTypes.shape({ current: PropTypes.func }).isRequired,
};

export default TitleSlide;
